import React, { useState, useRef, useCallback, useEffect } from 'react';

const APPS = [
  { id: 'ai-assistant', name: 'AI Assistant', icon: '/app-icons/1773068081484-trddgl06o.png', url: null, description: 'Helps create JIRA tickets' },
  { id: 'capital', name: 'Capital', icon: '/app-icons/1770987847799-0qpiuk5oh13p.png', url: 'https://capital.example.com', description: 'Fundraising and LP management' },
  { id: 'hidden-value', name: 'Hidden Value', icon: '/app-icons/1771433493938-adhpqkde89.png', url: 'https://hiddenvalue.example.com', description: 'Discover underpriced tokens' },
  { id: 'portfolio', name: 'Portfolio', icon: '/app-icons/1771355443130-akbvoly9uer.png', url: 'https://portfolio.example.com', description: 'View and analyze AUM' },
  { id: 'scout', name: 'Scout', icon: '/app-icons/1771354017321-ce9lqbk8kmb.png', url: 'https://scout.example.com', description: 'Discover promising founders' },
  { id: 'scrapecast', name: 'Scrapecast', icon: '/app-icons/1771091629698-v0li4dojsna.png', url: 'https://examplescrapecastproviderdomain.com', description: 'Podcasts from any source' },
  { id: 'thesis', name: 'Thesis', icon: '/app-icons/1771434168056-a5btuotm77.png', url: 'https://thesis.example.com', description: 'Monitoring dashboard' },
  { id: 'transcription', name: 'Transcription', icon: '/app-icons/1771357115421-376is00an5e.png', url: 'https://transcription.example.com', description: 'Audio/video to text' },
];

const STORAGE_KEY = 'hermes_app_order';
const LONG_PRESS_MS = 500;

function getStoredOrder() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {}
  return null;
}

function sortApps(apps) {
  const storedOrder = getStoredOrder();
  if (storedOrder) {
    const orderMap = {};
    storedOrder.forEach((id, i) => { orderMap[id] = i; });
    return [...apps].sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));
  }
  return [...apps].sort((a, b) => a.name.localeCompare(b.name));
}

export default function AllApps({ onBack }) {
  const [apps, setApps] = useState(() => sortApps(APPS));
  const [editMode, setEditMode] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [dragPos, setDragPos] = useState(null); // {x, y} for floating icon
  const gridRef = useRef(null);
  const longPressTimer = useRef(null);
  const touchStartPos = useRef(null);
  const itemRects = useRef([]); // cached bounding rects

  const saveOrder = useCallback((newApps) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newApps.map(a => a.id)));
  }, []);

  const cacheRects = useCallback(() => {
    if (!gridRef.current) return;
    const items = gridRef.current.querySelectorAll('.app-icon-wrapper');
    itemRects.current = Array.from(items).map(el => el.getBoundingClientRect());
  }, []);

  const getIdxAtPoint = useCallback((x, y) => {
    for (let i = 0; i < itemRects.current.length; i++) {
      const r = itemRects.current[i];
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return i;
    }
    return null;
  }, []);

  // --- Long press to enter edit mode ---
  const handleTouchStart = useCallback((e, idx) => {
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY, idx };
    longPressTimer.current = setTimeout(() => {
      // Enter edit mode and start dragging
      setEditMode(true);
      setDragIdx(idx);
      cacheRects();
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(10);
    }, LONG_PRESS_MS);
  }, [cacheRects]);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    // Cancel long press if moved too much before timer fires
    if (longPressTimer.current && touchStartPos.current) {
      const dx = touch.clientX - touchStartPos.current.x;
      const dy = touch.clientY - touchStartPos.current.y;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
    // If dragging, update position and detect target
    if (dragIdx !== null) {
      e.preventDefault();
      setDragPos({ x: touch.clientX, y: touch.clientY });
      const targetIdx = getIdxAtPoint(touch.clientX, touch.clientY);
      if (targetIdx !== null && targetIdx !== dragIdx) {
        setOverIdx(targetIdx);
      }
    }
  }, [dragIdx, getIdxAtPoint]);

  const handleTouchEnd = useCallback(() => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;

    if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
      setApps(prev => {
        const newApps = [...prev];
        const [moved] = newApps.splice(dragIdx, 1);
        newApps.splice(overIdx, 0, moved);
        saveOrder(newApps);
        return newApps;
      });
    }
    setDragIdx(null);
    setOverIdx(null);
    setDragPos(null);
    touchStartPos.current = null;
  }, [dragIdx, overIdx, saveOrder]);

  useEffect(() => {
    const opts = { passive: false };
    document.addEventListener('touchmove', handleTouchMove, opts);
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  // Desktop drag and drop
  const handleDragStart = (e, idx) => {
    if (!editMode) {
      setEditMode(true);
    }
    setDragIdx(idx);
    cacheRects();
    e.dataTransfer.effectAllowed = 'move';
    // Use transparent drag image
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (idx !== dragIdx) setOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newApps = [...apps];
    const [moved] = newApps.splice(dragIdx, 1);
    newApps.splice(idx, 0, moved);
    setApps(newApps);
    saveOrder(newApps);
    setDragIdx(null);
    setOverIdx(null);
    setDragPos(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
    setDragPos(null);
  };

  const handleAppClick = (app, idx) => {
    // If in edit mode, tapping elsewhere exits edit mode
    if (editMode && dragIdx === null) {
      setEditMode(false);
      return;
    }
    if (editMode) return;
    if (app.url === null) {
      onBack();
    } else {
      window.location.href = app.url;
    }
  };

  // Tap background to exit edit mode
  const handleBackgroundClick = (e) => {
    if (editMode && e.target === gridRef.current) {
      setEditMode(false);
    }
  };

  return (
    <div className="all-apps-page">
      <div className="all-apps-header">
        <button className="all-apps-back" onClick={onBack}>
          <span className="all-apps-back-arrow">&lsaquo;</span>
          <span className="all-apps-back-text">AI Assistant</span>
        </button>
        <span className="all-apps-title">All Apps</span>
      </div>
      <div className="all-apps-grid" ref={gridRef} onClick={handleBackgroundClick}>
        {apps.map((app, idx) => {
          const isDragging = dragIdx === idx;
          const isOver = overIdx === idx && dragIdx !== null && dragIdx !== idx;

          return (
            <div
              key={app.id}
              className={`app-icon-wrapper ${editMode ? 'edit-mode' : ''} ${isDragging ? 'dragging' : ''} ${isOver ? 'drag-over' : ''}`}
              draggable={editMode}
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, idx)}
              onClick={() => handleAppClick(app, idx)}
            >
              <div className="app-icon-img-wrapper">
                <img src={app.icon} alt={app.name} className="app-icon-img" draggable={false} />
              </div>
              <span className="app-icon-name">{app.name}</span>
            </div>
          );
        })}
      </div>

      {/* Floating drag ghost */}
      {dragIdx !== null && dragPos && (
        <div className="app-drag-ghost" style={{ left: dragPos.x - 30, top: dragPos.y - 30 }}>
          <div className="app-icon-img-wrapper">
            <img src={apps[dragIdx].icon} alt="" className="app-icon-img" />
          </div>
        </div>
      )}
    </div>
  );
}
